import {Timeline, TimelineProps} from "../Timeline/Timeline";
import {
    AcceptedTimelineEvent,
    adjustFromTimeZone,
    CanceledTimelineEvent,
    CompletedTimelineEvent,
    ContainerAlertEntity,
    containerAlertSelector,
    ContainerAlertTimelineEvent,
    DispatchDispatchRequestAssocEntity,
    DispatchRequestEntity,
    dispatchRequestStatus,
    DispatchRequestTimelineEvent,
    joinSafely,
    ReassignedTimelineEvent,
    RejectedTimelineEvent,
    RequestedTimelineEvent,
    TransportTimelineEvent,
    TransportType,
    transportType,
    UnresolvedTimelineEvent,
    useGetContainerAlertQuery
} from "@ots-frontend/ots-data";
import {TransportTimelineItem} from "./TransportTimelineItem";
import {ContainerAlertTimelineItem} from "./ContainerAlertTimelineItem";
import {RequestedTimelineItem} from "./RequestedTimelineItem";
import {AcceptedTimelineItem} from "./AcceptedTimelineItem";
import {RejectedTimelineItem} from "./RejectedTimelineItem";
import {ReassignedTimelineItem} from "./ReassignedTimlineItem";
import {CanceledTimelineItem} from "./CanceledTimelineItem";
import {UnresolvedTimelineItem} from "./UnresolvedTimelineItem";
import {CompletedTimelineItem} from "./CompletedTimelineItem";
import {useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";

interface TransportEvent {
    date: number;
    user?: string;
    type: TransportType;
    entity: DispatchDispatchRequestAssocEntity;
}

const getTimelineItem = (
    t: TFunction<"common"[], undefined, "common"[]>,
    event: DispatchRequestTimelineEvent
): React.ReactNode => {
    switch (event.type) {
        case 'TRANSPORT':
            return (
                <Timeline.Item
                    key={`transport-${event.id}`}
                    title={<TransportTimelineItem.Title event={event}/>}
                    bullet={<TransportTimelineItem.Bullet event={event}/>}
                >
                    <TransportTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'CONTAINER_ALERT':
            return (
                <Timeline.Item
                    key={`container-alert-${event.date.toString()}`}
                    title={<ContainerAlertTimelineItem.Title/>}
                    bullet={<ContainerAlertTimelineItem.Bullet/>}
                >
                    <ContainerAlertTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'REQUESTED':
            return (
                <Timeline.Item
                    key={`requested-${event.date.toString()}`}
                    title={<RequestedTimelineItem.Title/>}
                    bullet={<RequestedTimelineItem.Bullet/>}
                >
                    <RequestedTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'ACCEPTED':
            return (
                <Timeline.Item
                    key={`accepted-${event.date.toString()}`}
                    title={<AcceptedTimelineItem.Title/>}
                    bullet={<AcceptedTimelineItem.Bullet/>}
                >
                    <AcceptedTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'REJECTED':
            return (
                <Timeline.Item
                    key={`rejected-${event.date.toString()}`}
                    title={<RejectedTimelineItem.Title/>}
                    bullet={<RejectedTimelineItem.Bullet/>}
                >
                    <RejectedTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'REASSIGNED':
            return (
                <Timeline.Item
                    key={`reassigned-${event.date.toString()}`}
                    title={<ReassignedTimelineItem.Title/>}
                    bullet={<ReassignedTimelineItem.Bullet/>}
                >
                    <ReassignedTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'CANCELED':
            return (
                <Timeline.Item
                    key={`canceled-${event.date.toString()}`}
                    title={<CanceledTimelineItem.Title/>}
                    bullet={<CanceledTimelineItem.Bullet/>}
                >
                    <CanceledTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'UNRESOLVED':
            return (
                <Timeline.Item
                    key={`unresolved-${event.date.toString()}`}
                    title={<UnresolvedTimelineItem.Title/>}
                    bullet={<UnresolvedTimelineItem.Bullet/>}
                >
                    <UnresolvedTimelineItem event={event}/>
                </Timeline.Item>
            );
        case 'COMPLETED':
          return (
            <Timeline.Item
              key={`completed`}
              title={<CompletedTimelineItem.Title/>}
              bullet={<CompletedTimelineItem.Bullet/>}
            >
              <CompletedTimelineItem event={event}/>
            </Timeline.Item>
          )
        default:
            return undefined;
    }
};

const getTimelineEvents = (
    dispatchRequest: DispatchRequestEntity,
    containerAlert?: ContainerAlertEntity
): DispatchRequestTimelineEvent[] => {
    const events: DispatchRequestTimelineEvent[] = [];

    let latestDttm = dispatchRequest.createDttm;
    if (latestDttm) {
        if (dispatchRequest.containerAlertId) {
            const event: ContainerAlertTimelineEvent = {
                id: dispatchRequest.containerAlertId,
                date: containerAlert?.alertDttm ?? latestDttm,
                alertType: containerAlert?.alertType,
                type: 'CONTAINER_ALERT',
            };
            events.push(event);
        }

        const requestedEvent: RequestedTimelineEvent = {
            person: dispatchRequest.createdBy,
            acceptTimeLimit: dispatchRequest.acceptTimeLimit,
            date: latestDttm,
            type: 'REQUESTED',
        };
        events.push(requestedEvent);

        if (
            dispatchRequest.acceptedDttm ||
            dispatchRequest.status === dispatchRequestStatus.accepted
        ) {
            if (dispatchRequest.acceptedDttm) {
                latestDttm = dispatchRequest.acceptedDttm;
            }
            const event: AcceptedTimelineEvent = {
                person: joinSafely(" ", dispatchRequest.acceptedByFirstName, dispatchRequest.acceptedByLastName),
                company: dispatchRequest.truckingCompanyName,
                date: latestDttm,
                type: 'ACCEPTED',
            };
            events.push(event);
        }

        if (
            dispatchRequest.rejectedDttm ||
            dispatchRequest.status === dispatchRequestStatus.rejected
        ) {
            if (dispatchRequest.rejectedDttm) {
                latestDttm = dispatchRequest.rejectedDttm;
            }
            const event: RejectedTimelineEvent = {
                person: joinSafely(" ", dispatchRequest.rejectedByFirstName, dispatchRequest.rejectedByLastName),
                date: latestDttm,
                type: 'REJECTED',
            };
            events.push(event);
        }

        if (
            dispatchRequest.reassignedDttm ||
            dispatchRequest.status === dispatchRequestStatus.reassigned
        ) {
            if (dispatchRequest.reassignedDttm) {
                latestDttm = dispatchRequest.reassignedDttm;
            }
            const event: ReassignedTimelineEvent = {
                person: joinSafely(" ", dispatchRequest.reassignedByFirstName, dispatchRequest.reassignedByLastName),
                company: dispatchRequest.reassignedToTruckCompanyName,
                date: latestDttm,
                reassignedToId: dispatchRequest.reassignedToId,
                type: 'REASSIGNED',
            };
            events.push(event);
        }

        if (
            dispatchRequest.canceledDttm ||
            dispatchRequest.status === dispatchRequestStatus.canceled
        ) {
            if (dispatchRequest.canceledDttm) {
                latestDttm = dispatchRequest.canceledDttm;
            }
            const event: CanceledTimelineEvent = {
                person: joinSafely(" ", dispatchRequest.canceledByFirstName, dispatchRequest.canceledByLastName),
                date: latestDttm,
                type: 'CANCELED',
            };
            events.push(event);
        }

        if (
            dispatchRequest.unresolvedDttm ||
            dispatchRequest.status === dispatchRequestStatus.unresolved
        ) {
            if (dispatchRequest.unresolvedDttm) {
                latestDttm = dispatchRequest.unresolvedDttm;
            }
            const event: UnresolvedTimelineEvent = {
                person: joinSafely(" ", dispatchRequest.unresolvedByFirstName, dispatchRequest.unresolvedByLastName),
                date: latestDttm,
                type: 'UNRESOLVED',
            };
            events.push(event);
        }

        const transportEvents: TransportEvent[] = [];
        for (const job of dispatchRequest.dispatches) {
            if (job.pickupDttm) {
                transportEvents.push({
                    date: job.pickupDttm,
                    user: job.driverName,
                    type: transportType.pickup,
                    entity: job,
                });
            }

            if (job.deliveryDttm) {
                transportEvents.push({
                    date: job.deliveryDttm,
                    user: job.driverName,
                    type: transportType.delivery,
                    entity: job,
                });
            }
        }

        // sort the events by date
        transportEvents.sort(
            (a: TransportEvent, b: TransportEvent) =>
                a.date - b.date
        );

        for (const transportEvent of transportEvents) {
            const event: TransportTimelineEvent = {
                id: transportEvent.date.toString(),
                date: transportEvent.date,
                transportType: transportEvent.type,
                person: transportEvent.user,
                type: 'TRANSPORT',
            };
            events.push(event);
        }

        if (dispatchRequest.status === dispatchRequestStatus.completed) {
            const event: CompletedTimelineEvent = {
                id: 'completed',
                type: 'COMPLETED',
            };
            events.push(event);
        }
    }

    return events;
};

export type DispatchRequestTimelineProps = Omit<TimelineProps, 'children'> & {
    dispatchRequest: DispatchRequestEntity
}

export const DispatchRequestTimeline = (props: DispatchRequestTimelineProps) => {
    const {dispatchRequest, ...rest} = props

    const {t} = useTranslation(["common"])

    const selectContainerAlert = useMemo(() => {
        return containerAlertSelector()
    }, []);

    const {containerAlert} = useGetContainerAlertQuery(dispatchRequest.containerAlertId ?? skipToken, {
        selectFromResult: (result) => ({
            containerAlert: selectContainerAlert(result)
        })
    });

    const events = useMemo(
        () => getTimelineEvents(dispatchRequest, containerAlert),
        [dispatchRequest, containerAlert]
    );

    const nodes = useMemo(() => events.map((e) => getTimelineItem(t, e)), [events, t]);

    return (
        <Timeline {...rest}>
            {nodes.map((n) => {
                return n;
            })}
        </Timeline>
    )
}
